import axios from 'axios';
import { auth } from '../firebase';

const API_BASE = process.env.REACT_APP_API_URL || 'https://clicksaver.app';
const api = axios.create({ baseURL: API_BASE, timeout: 30000 });

// Always attach a fresh Firebase token
api.interceptors.request.use(async config => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  me: () => api.get('/api/auth/me').then(r => r.data),
  updateSettings: (data) => api.patch('/api/auth/settings', data).then(r => r.data),
  sendWelcome: () => api.post('/api/auth/welcome').then(r => r.data),
};

export const clicksAPI = {
  getRecent: (id, limit = 50) => api.get(`/api/clicks/recent/${id}?limit=${limit}`).then(r => r.data),
  getBlocked: (id) => api.get(`/api/clicks/blocked/${id}`).then(r => r.data),
  unblock: (id, ip) => api.delete(`/api/clicks/unblock/${id}/${ip}`).then(r => r.data),
  blockIP: (id, ip, note) => api.post(`/api/clicks/block/${id}`, { ip, note }).then(r => r.data),
  testRules: (id, ip, opts = {}) => api.post(`/api/clicks/test/${id}`, { ip, ...opts }).then(r => r.data),
  runTests: (id) => api.post(`/api/clicks/run-tests/${id}`).then(r => r.data),
};

export const dashboardAPI = {
  getStats: (id, days = 30, startDate = null, endDate = null) => {
    let url = `/api/dashboard/stats/${id}?days=${days}`;
    if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
    return api.get(url).then(r => r.data);
  },
  getTopThreats: (id) => api.get(`/api/dashboard/top-threats/${id}`).then(r => r.data),
  getCampaignFraud: (id, days = 30, startDate = null, endDate = null) => {
    let url = `/api/dashboard/campaign-fraud/${id}?days=${days}`;
    if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
    return api.get(url).then(r => r.data);
  },
  getCombined: (id, days = 30, startDate = null, endDate = null) => {
    let url = `/api/dashboard/combined/${id}?days=${days}`;
    if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
    return api.get(url).then(r => r.data);
  },
};

export const campaignsAPI = {
  list: () => api.get('/api/campaigns/').then(r => r.data),
  get: (id) => api.get(`/api/campaigns/${id}`).then(r => r.data),
  create: (data) => api.post('/api/campaigns/', data).then(r => r.data),
  update: (id, data) => api.patch(`/api/campaigns/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/api/campaigns/${id}`).then(r => r.data),
  verify: (id) => api.get(`/api/campaigns/${id}/verify`, { timeout: 35000 }).then(r => r.data),
  getTrackingScript: (id) => api.get(`/api/campaigns/${id}/tracking-script`).then(r => r.data),
};

export const analyticsAPI = {
  getFraud: (id, days = 30) => api.get(`/api/analytics/fraud/${id}?days=${days}`).then(r => r.data),
  getBlockedSummary: (id) => api.get(`/api/analytics/blocked-summary/${id}`).then(r => r.data),
};

export const billingAPI = {
  getStatus: () => api.get('/api/billing/status').then(r => r.data),
  getUsage: () => api.get('/api/billing/usage').then(r => r.data),
  getInvoices: (limit = 24) => api.get(`/api/billing/invoices?limit=${limit}`).then(r => r.data),
  getInvoice: (id) => api.get(`/api/billing/invoices/${id}`).then(r => r.data),
  getInvoicePdfUrl: (id) => `${API_BASE}/api/billing/invoices/${id}/pdf`,
  createCheckout: (data) => api.post('/api/billing/checkout', data).then(r => r.data),
  createPortal: () => api.post('/api/billing/portal').then(r => r.data),
  cancelSubscription: () => api.post('/api/billing/cancel').then(r => r.data),
  syncPlan: () => api.post('/api/billing/sync').then(r => r.data),
  reactivate: () => api.post('/api/billing/reactivate').then(r => r.data),
  changePlan: (price_id, discount_code) => api.post('/api/billing/change-plan', { price_id, discount_code }).then(r => r.data),
  updateDetails: (data) => api.patch('/api/billing/details', data).then(r => r.data),
};

export const reportsAPI = {
  sendTest: (type) => api.post(`/api/reports/send/${type}`).then(r => r.data),
  testEmail: (email) => api.post('/api/reports/send/test-email', { email }).then(r => r.data),
  getHistory: () => api.get('/api/reports/history').then(r => r.data),
};

export const notificationsAPI = {
  get: () => api.get('/api/auth/notifications').then(r => r.data),
  update: (data) => api.patch('/api/auth/notifications', data).then(r => r.data),
};

export const googleAdsAPI = {
  listCampaigns: (campaignId) => api.get(`/api/google-ads/campaigns${campaignId ? '?campaign_id=' + campaignId : ''}`).then(r => r.data),
  listAccounts: (campaignId) => api.get(`/api/google-ads/accounts${campaignId ? '?campaign_id=' + campaignId : ''}`).then(r => r.data),
  getPerformance: (campaignId, startDate, endDate, days = 30) => {
    const params = new URLSearchParams();
    if (campaignId) params.set('campaign_id', campaignId);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (!startDate && days !== 30) params.set('days', days);
    const qs = params.toString();
    return api.get(`/api/google-ads/performance${qs ? '?' + qs : ''}`).then(r => r.data);
  },
  getStatus: (id) => api.get(`/api/google-ads/status/${id}`).then(r => r.data),
  getExclusions: (id) => api.get(`/api/google-ads/exclusions/${id}`).then(r => r.data),
  sync: (id) => api.post(`/api/google-ads/sync/${id}`).then(r => r.data),
  linkCampaign: (id, data) => api.patch(`/api/google-ads/link/${id}`, data).then(r => r.data),
  // Auto-sync schedule
  getSchedule: (campaignId) => api.get(`/api/google-ads/schedule${campaignId ? '?campaign_id=' + campaignId : ''}`).then(r => r.data),
  updateSchedule: (schedule, campaignId, rollingWindowDays) => api.patch('/api/google-ads/schedule', { schedule, ...(campaignId ? { campaign_id: campaignId } : {}), ...(rollingWindowDays != null ? { rolling_window_days: rollingWindowDays } : {}) }).then(r => r.data),
  syncNow: (campaignId) => api.post(`/api/google-ads/sync-now${campaignId ? '?campaign_id=' + campaignId : ''}`).then(r => r.data),
  // OAuth connection
  getAuthStatus: (campaignId) => api.get(`/api/auth/google/status${campaignId ? '?campaign_id=' + campaignId : ''}`).then(r => r.data),
  getAuthUrl: (campaignId) => api.get(`/api/auth/google/connect${campaignId ? '?campaign_id=' + campaignId : ''}`).then(r => r.data),
  disconnect: (campaignId) => api.delete(`/api/auth/google/disconnect${campaignId ? '?campaign_id=' + campaignId : ''}`).then(r => r.data),
  saveCustomer: (data) => api.patch('/api/auth/google/customer', data).then(r => r.data),
};

export const allowlistAPI = {
  list: (campaignId) => api.get(`/api/allowlist/${campaignId}`).then(r => r.data),
  add: (campaignId, data) => api.post(`/api/allowlist/${campaignId}`, data).then(r => r.data),
  remove: (campaignId, entryId) => api.delete(`/api/allowlist/${campaignId}/${entryId}`).then(r => r.data),
};



export const gadsScriptAPI = {
  getDashboard: (campaignId, startDate, endDate) => {
    const params = new URLSearchParams();
    if (campaignId) params.set('campaign_id', campaignId);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    const qs = params.toString();
    return api.get(`/api/gads-script/dashboard-data${qs ? '?' + qs : ''}`).then(r => r.data);
  },
  getSettings: () => api.get('/api/gads-script/settings').then(r => r.data),
  generateKey: () => api.post('/api/gads-script/generate-key').then(r => r.data),
  updateSettings: (data) => api.patch('/api/gads-script/settings', data).then(r => r.data),
};
